import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import LangEnum from '../../../lang/LangEnum';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { existsIndexDbToUser } from '../../../modules/offline/database/offlineDatabase/offlineDatabase';
import { onlineOrderRepositoryOfflineAdapters } from '../../../modules/offline/offlineRequestAdapters/onlineOrder.repository.offlineAdapters';
import { offlineOrdersRepository } from '../../../modules/offline/repositories/offlineOrders.repository';
import { Order } from '../../../modules/orders/order.types';
import { ordersRepository } from '../../../modules/orders/repositories/ordersRepository/orders.repository';
import { invalidateAffectedOrderProductsGridQueries } from '../../../modules/orders/utils/orderCalculate.utils';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import PATHS from '../../../routes/routes.const';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import { selectIsInStoreEnvironment } from '../../../stores/AppEnvironment/appEnvironment.store.selectors';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { HTTP_STATUS } from '../../../utils/httpStatus.const';
import {
  getOfflineOrderHistoriesKey,
  getOrderQueryKey,
  getOrderSummaryQueryKey,
} from './order.keys';
import { getOrderSummary } from './order.repository';
import { GetOrderSummaryOutput, GetOrderSummaryParams } from './order.types';

export const useGetOrderSummary = (
  params: GetOrderSummaryParams,
  options?: UseDataQueryOptions,
) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const queryFn = async () => {
    const response = await getOrderSummary(params);
    if (!response.ok || !response.data) {
      return;
    }

    if (response.data.affectedOrderProducts?.length) {
      invalidateAffectedOrderProductsGridQueries({
        queryClient,
        teamId: team?.id,
        orderId: params.orderId,
        isOfflineMode: params.isOfflineMode,
        affectedOrderProducts: response.data.affectedOrderProducts,
      });
    }

    return response.data;
  };

  return useDataQuery<GetOrderSummaryOutput | undefined>(
    {
      queryKey: getOrderSummaryQueryKey(params),
      queryFn,
      refetchOnMount: true,
      gcTime: 0,
    },
    options,
  );
};

export const useGetOrderPaymentData = (params: {
  orderId: string;
  isOpen: boolean;
}) => {
  const { translate } = useTranslation();
  const { isOpen, orderId } = params;

  return useDataQuery(
    {
      queryKey: ['orderPaymentData', orderId],
      queryFn: async () => {
        const response = await ordersRepository(orderId).getOrderPaymentData();

        if (response.ok && response.data) {
          return response.data;
        }

        snackbarStore.handleMessage({
          type: 'error',
          message: translate(LangEnum.SNACKBAR_REFRESH_PAGE),
        });
      },
    },
    { enabled: isOpen },
  );
};

export const useGetOrderPaymentHistory = (params: {
  orderId: string;
  isOpen: boolean;
}) => {
  const { translate } = useTranslation();
  const { isOpen, orderId } = params;

  return useDataQuery(
    {
      queryKey: ['orderPaymentHistory', orderId],
      queryFn: async () => {
        const response = await ordersRepository(orderId).getPaymentHistory();

        if (response.ok && response.data) {
          return response.data;
        }

        snackbarStore.handleMessage({
          type: 'error',
          message: translate(LangEnum.SNACKBAR_REFRESH_PAGE),
        });
      },
    },
    { enabled: isOpen },
  );
};

export const useGetOfflineOrderHistories = (orderId: string) =>
  useDataQuery(
    {
      queryKey: getOfflineOrderHistoriesKey({ orderId }),
      queryFn: async () => {
        const response = await onlineOrderRepositoryOfflineAdapters(orderId).getOrderHistories();
        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('offline order histories request failed');
        }
        return response.data.data;
      },
      gcTime: 0,
    },
    { enabled: Boolean(orderId) },
  );

export const useGetOrderById = ({ id, enabled }: { id: string; enabled?: boolean }) => {
  const history = useHistory();

  const findOfflineOrderIfExists = async () => {
    const userHasOfflineOrders =
      (await existsIndexDbToUser()) && (await offlineOrdersRepository.hasOrders());
    if (!userHasOfflineOrders) {
      return;
    }

    const offlineOrderRes = await onlineOrderRepositoryOfflineAdapters(id).getOne();
    if (offlineOrderRes) {
      return offlineOrderRes.data as Order<true>;
    }
  };

  const orderDataQueryFn = async (): Promise<Order<true | false> | undefined> => {
    const response = await ordersRepository(id).getOne();

    const canTryFindInOffline = !response.ok && response.data?.status === HTTP_STATUS.NOT_FOUND;
    const offlineOrder = canTryFindInOffline ? await findOfflineOrderIfExists() : undefined;
    if (offlineOrder) {
      return offlineOrder;
    }

    if (!response.ok) {
      snackbarStore.handleMessage({
        type: 'error',
        message: response.data?.message,
      });
      history.push(PATHS.main.home.path);
      return;
    }

    const isInStoreEnvironment = selectIsInStoreEnvironment(useAppEnvironmentStore.getState());
    const orderbookStore = useOrderBookStore.getState();
    const isOrderInOrderBook = orderbookStore.selectedOrder?.id === id;
    if (!isInStoreEnvironment && isOrderInOrderBook) {
      orderbookStore.handleChangeSelectedOrder({ order: response.data });
    }

    return response.data as Order;
  };

  return useDataQuery(
    {
      queryKey: getOrderQueryKey({ orderId: id }),
      queryFn: orderDataQueryFn,
      gcTime: 0,
    },
    { enabled },
  );
};
